import React from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Link as RouterLink } from 'react-router-dom';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { Button, useMediaQuery, useTheme, TableContainer, Paper, Box, Grid, Card, CardContent, CardActions } from '@material-ui/core';

// Internal dependencies
import { routes, formatNumber } from '../../../constants.js/misc';
import OrderOriginLabel from '../../OrderOrigin/OrderOriginLabel';

/**
 * Renders the orders table
 * @param {function} setOrderToEditsss sets the orderToEdit object on the state
 */

const ClientOrderTable = ({ orderDetails, storeData }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const totalWithShipping = (orderShippingCost) => {
        return orderShippingCost
            ? (isNaN(orderShippingCost)
                ? ((isNaN(storeData.delivery_cost_details) || !storeData.delivery_cost_enabled) ? 0 : parseInt(storeData.delivery_cost_details))
                : orderShippingCost
            )
            : (isNaN(storeData.delivery_cost_details) || !storeData.delivery_cost_enabled) ? 0 : parseInt(storeData.delivery_cost_details);
    }

    // Mobile Card View Component
    const MobileOrderCard = ({ orderDetail }) => (
        <Card 
            key={orderDetail?.id} 
            elevation={2}
        >
            <CardContent style={{ padding: '16px' }}>
                <Grid container spacing={2}>
                    {/* Order Number and Date */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                            <Link
                                component={RouterLink}
                                to={routes.ordersPage + '/' + orderDetail.id}
                                color="textSecondary"
                                variant='button'
                                style={{ fontWeight: 'bold', fontSize: '16px' }}
                            >
                                #{orderDetail.sequence}
                            </Link>
                            <Typography variant="body2" color="textSecondary">
                                {new Date(orderDetail.created_at).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Order Value */}
                    <Grid item xs={12}>
                        <Box>
                            <Typography variant="caption" color="textSecondary" component="div">
                                {t('ClientOrdersTablePage.orders.headers.orderValue')}
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                                {formatNumber(orderDetail.order_details.reduce((acc, detail) => acc + (detail.price * detail.quantity), 0) + totalWithShipping(orderDetail.shipping_cost), storeData.currency)}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Line/Branch */}
                    <Grid item xs={12}>
                        <Box>
                            <Typography variant="caption" color="textSecondary" component="div">
                                {t('ClientOrdersTablePage.orders.headers.line')}
                            </Typography>
                            <OrderOriginLabel order={orderDetail} />
                        </Box>
                    </Grid>

                    {/* Status Information */}
                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="caption" color="textSecondary" component="div">
                                {t('ClientOrdersTablePage.orders.headers.orderStatus')}
                            </Typography>
                            <Typography variant="body2">
                                {orderDetail?.order_status?.name}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="caption" color="textSecondary" component="div">
                                {t('ClientOrdersTablePage.orders.headers.deliveryStatus')}
                            </Typography>
                            <Typography variant="body2">
                                {orderDetail?.shipping_order_status?.name}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions style={{ padding: '8px 16px 16px 16px', justifyContent: 'flex-end' }}>
                <Button
                    component={RouterLink}
                    to={routes.ordersPage + '/' + orderDetail.id}
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {t('OrdersTablePage.headers.btnOrder')}
                </Button>
            </CardActions>
        </Card>
    );

    // Desktop/Tablet Table View
    const DesktopTable = () => (
        <TableContainer component={Paper}>
            <Table stickyHeader={true}>
                <TableHead>
                    <TableRow>
                        <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                            {t('ClientOrdersTablePage.orders.headers.order')}
                        </TableCell>
                        <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                            {t('ClientOrdersTablePage.orders.headers.date')}
                        </TableCell>
                        <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                            {t('ClientOrdersTablePage.orders.headers.orderValue')}
                        </TableCell>
                        <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                            {t('ClientOrdersTablePage.orders.headers.line')}
                        </TableCell>
                        {!isTablet && (
                            <TableCell style={{ fontSize: '14px' }}>
                                {t('ClientOrdersTablePage.orders.headers.orderStatus')}
                            </TableCell>
                        )}
                        {!isTablet && (
                            <TableCell style={{ fontSize: '14px' }}>
                                {t('ClientOrdersTablePage.orders.headers.deliveryStatus')}
                            </TableCell>
                        )}
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {orderDetails?.map(orderDetail =>
                        <TableRow key={orderDetail?.id}>
                            <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                                <Link
                                    component={RouterLink}
                                    to={routes.ordersPage + '/' + orderDetail.id}
                                    color="textSecondary"
                                    variant='button'
                                >
                                    #{orderDetail.sequence}
                                </Link>
                            </TableCell>
                            <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                                {new Date(orderDetail.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                                {formatNumber(orderDetail.order_details.reduce((acc, detail) => acc + (detail.price * detail.quantity), 0) + totalWithShipping(orderDetail.shipping_cost), storeData.currency)}
                            </TableCell>
                            <TableCell style={{ fontSize: isTablet ? '12px' : '14px' }}>
                            <OrderOriginLabel order={orderDetail} />
                            </TableCell>
                            {!isTablet && (
                                <TableCell style={{ fontSize: '14px' }}>
                                    {orderDetail?.order_status?.name}
                                </TableCell>
                            )}
                            {!isTablet && (
                                <TableCell style={{ fontSize: '14px' }}>
                                    {orderDetail?.shipping_order_status?.name}
                                </TableCell>
                            )}
                            <TableCell>
                                <Button
                                    component={RouterLink}
                                    to={routes.ordersPage + '/' + orderDetail.id}
                                    variant="contained"
                                    color="primary"
                                    size={isTablet ? "small" : "medium"}
                                    style={{ 
                                        whiteSpace: 'nowrap',
                                        fontSize: isTablet ? '11px' : '14px',
                                        padding: isTablet ? '4px 8px' : '6px 16px'
                                    }}
                                >
                                    {t('OrdersTablePage.headers.btnOrder')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Render mobile cards or desktop table based on screen size
    if (isMobile) {
        return (
            <>
                {orderDetails?.map(orderDetail => 
                    <MobileOrderCard key={orderDetail?.id} orderDetail={orderDetail} />
                )}
            </>
        );
    }

    return <DesktopTable />;
};

const mapStateToProps = ({ common }) => ({
    storeData: common.storeData
});

export default connect(
    mapStateToProps,
)(ClientOrderTable);